import { useState } from 'react';
import type { Tenant } from '@provalia/shared';
import { ErrorBox, Modal, useAsync, useToast } from '../../shared/ui';
import { api } from '../api';
import type { Summary } from '../types';
import { EMPTY_TENANT, TenantForm, validateTenant, type TenantErrors } from './TenantForm';

export function NewTenantModal({
  domain,
  onClose,
  onCreated,
}: {
  domain: string;
  onClose: () => void;
  onCreated: (slug: string) => void;
}): JSX.Element {
  const toast = useToast();
  const [draft, setDraft] = useState(EMPTY_TENANT);
  const [errors, setErrors] = useState<TenantErrors>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const summary = useAsync(() => api.get<Summary>('/admin/summary'), []);

  const submit = async (): Promise<void> => {
    const { input, errors: found } = validateTenant(draft);
    setErrors(found);
    if (!input) return;
    setBusy(true);
    setError(null);
    try {
      const { tenant } = await api.post<{ tenant: Tenant }>('/admin/tenants', input);
      toast(`Cliente ${tenant.name} dado de alta`, 'ok');
      onCreated(tenant.slug);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Nuevo cliente"
      wide
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Creando…' : 'Dar de alta'}
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="muted">
          Se crea el cliente con su esquema de datos vacío. Después, en su ficha, se añaden las sociedades de SAP, se prueba la conexión
          y se crean los campos U_EDI_.
        </p>
        <TenantForm draft={draft} onChange={setDraft} errors={errors} mode="create" domain={summary.data?.tenantDomain ?? domain} />
        <ErrorBox error={error} />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
